<template>
  <nav class="navbar glass-card">
    <div class="navbar__brand" @click="showPage('home')">
      <img src="/logo.svg" alt="logo" class="navbar__logo" />
      <span class="navbar__title">WPS AI Video</span>
      <span class="navbar__beta">BETA</span>
    </div>
    <div class="navbar__actions">
      <button
        v-if="state.currentPage === 'home'"
        class="navbar__feedback-btn"
        @click="onFeedbackClick"
      >
        <i class="fa-solid fa-comment-dots navbar__feedback-icon"></i>
        反馈
      </button>
      <!-- 已登录：头像+昵称+下拉菜单 -->
      <div v-if="authState.loggedIn" class="navbar__user-wrap"
           @mouseenter="userMenuOpen = true"
           @mouseleave="userMenuOpen = false">
        <div class="navbar__user" @click="onUserAvatarClick">
          <img :src="authState.avatar" :alt="authState.nickname"
               class="navbar__avatar"
               v-if="authState.avatar">
          <div v-else class="navbar__avatar-placeholder">
            <i class="fa-solid fa-user navbar__avatar-icon"></i>
          </div>
          <span class="navbar__nickname">{{ authState.nickname }}</span>
          <span v-if="state.quota.total > 0" class="navbar__quota" :class="{ 'navbar__quota--empty': state.quota.available <= 0 }">{{ state.quota.available }}次</span>
        </div>
        <div v-show="userMenuOpen" class="navbar__dropdown">
          <div class="navbar__dropdown-menu">
            <button class="navbar__dropdown-item navbar__dropdown-item--danger" @click="onLogoutClick">
              <i class="fa-solid fa-right-from-bracket navbar__dropdown-icon"></i>
              退出登录
            </button>
          </div>
        </div>
      </div>

      <!-- 未登录：登录按钮 -->
      <button v-else-if="authState.checked" @click="onLoginClick" class="navbar__login-btn">
        登录
      </button>

      <!-- 加载中占位 -->
      <div v-else class="navbar__avatar-placeholder">
        <i class="fa-solid fa-user navbar__avatar-icon"></i>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useAppStore } from '../composables/useAppStore'
import { useAuth } from '../composables/useAuth'
import { reportClick } from '../util/report'

const { showPage, state, clearDraft } = useAppStore()
const { authState, redirectToLogin } = useAuth()

const userMenuOpen = ref(false)

const reportNavPageName = computed(() => {
  if (state.currentPage === 'session') return 'detail'
  return 'index'
})

function onLoginClick() {
  reportClick('index', 'log_in')
  setTimeout(() => redirectToLogin(), 100)
}

function onUserAvatarClick() {
  reportClick(reportNavPageName.value, 'user_avatar')
}

function onFeedbackClick() {
  reportClick(reportNavPageName.value, 'feedback')
  openFeedback()
}

function onLogoutClick() {
  userMenuOpen.value = false
  reportClick(reportNavPageName.value, 'log_out')
  doLogout()
}

function openFeedback() {
  userMenuOpen.value = false
  const product_id = 8000036
  const product_name = encodeURIComponent('AI视频')
  const app_name = 'aivideo'
  const qs = `product_id=${product_id}&product_name=${product_name}&tofeedback=true&app_name=${app_name}`
  window.open(`https://get.wps.cn/feedback/pc?${qs}`, '_blank')
}

async function doLogout() {
  userMenuOpen.value = false
  clearDraft()
  try {
    await fetch('https://account.wps.cn/passport/secure/api/logout', {
      method: 'POST',
      credentials: 'include',
    })
  } catch { /* ignore */ }
  window.location.href = '/aivideo/'
}
</script>

<style scoped>
.navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 32px;
  position: sticky;
  top: 0;
  z-index: 50;
}

.navbar__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.navbar__logo {
  width: 32px;
  height: 32px;
}

.navbar__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: -0.025em;
}

.navbar__beta {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 6px;
  background: #eff6ff;
  color: #2563eb;
  font-weight: 600;
  border: 1px solid #bfdbfe;
}

.navbar__actions {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}

.navbar__link {
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  transition: color 0.2s;
}
.navbar__link:hover {
  color: #111827;
}

.navbar__feedback-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
}

.navbar__feedback-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.navbar__feedback-icon {
  font-size: 13px;
}

.navbar__user {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.navbar__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}
.navbar__user:hover .navbar__avatar {
  border-color: #3b82f6;
}

.navbar__avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
}

.navbar__avatar-icon {
  font-size: 12px;
  color: #6b7280;
}

.navbar__nickname {
  color: #4b5563;
  font-size: 13px;
  transition: color 0.2s;
}
.navbar__user:hover .navbar__nickname {
  color: #111827;
}

.quota-badge {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 9999px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1.5;
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  transition: all 0.3s;
}

.quota-badge--empty {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.navbar__user-wrap {
  position: relative;
}

.navbar__dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  padding-top: 8px;
  z-index: 100;
}

.navbar__dropdown-menu {
  min-width: 160px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
  padding: 6px 0;
}

.navbar__dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 18px;
  font-size: 14px;
  color: #374151;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
}

.navbar__dropdown-item:hover {
  background: #f5f5f5;
}

.navbar__dropdown-item--danger {
  color: #374151;
}

.navbar__dropdown-item--danger:hover {
  background: #fef2f2;
  color: #dc2626;
}

.navbar__dropdown-icon {
  font-size: 14px;
  width: 18px;
  text-align: center;
  color: #9ca3af;
}

.navbar__dropdown-item:hover .navbar__dropdown-icon {
  color: #6b7280;
}

.navbar__dropdown-item--danger:hover .navbar__dropdown-icon {
  color: #dc2626;
}

.navbar__dropdown-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 4px 0;
}

.navbar__quota {
  font-size: 12px;
  font-weight: 500;
  color: #3b82f6;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 2px 8px;
  white-space: nowrap;
  line-height: 1.4;
}

.navbar__quota--empty {
  color: #ef4444;
  background: #fef2f2;
  border-color: #fecaca;
}

.navbar__login-btn {
  padding: 6px 16px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: #111827;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}
.navbar__login-btn:hover {
  background: #1f2937;
}

@media (max-width: 640px) {
  .navbar__nickname {
    display: none;
  }
}
</style>
